import { BaseLineItem, PacingBlockV2 } from 'shared/src/api/line-item-types';
import { formatDate } from 'shared/src/date-string-utils/date-utils';
import { z } from 'zod';
import { equalToTwoDecimalPlaces } from 'shared/src/utils/number/number-utils';
import { ISODateString } from 'shared/src/date-string-utils/types';

type PacingTypes = {
  pacing_type: BaseLineItem['pacing_type'];
  pacing_details?: BaseLineItem['pacing_details'];
  monthly_rollover?: BaseLineItem['monthly_rollover'];
  dates?: BaseLineItem['dates'];
};

export function refineBasePacing<T extends PacingTypes>(data: T, ctx: z.RefinementCtx) {
  if (data.pacing_type === 'lifetime' && data.monthly_rollover === false) {
    ctx.addIssue({
      code: 'custom',
      message: 'Lifetime pacing requires monthly rollover to be enabled',
      path: ['monthly_rollover']
    });
  }

  if (!data.pacing_details || data.pacing_details.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'At least one pacing entry is required',
      path: ['pacing_details']
    });
    return;
  }

  const normalized = data.pacing_details.map((p, idx) => ({ idx, ...p.dates }));

  // Basic per-range check: from must be on/before to
  // With ISODateString, string comparison works for dates (lexicographic)
  normalized.forEach(({ idx, from, to }) => {
    if (from > to) {
      ctx.addIssue({
        code: 'custom',
        message: 'Start date must be on or before end date',
        path: ['pacing_details', idx, 'dates']
      });
    }
  });

  // Sort by start date so we can detect overlaps and attach error to the later entry
  const sorted = [...normalized].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));

  // Keep track of the interval with the furthest end we've seen so far
  let last = sorted[0];

  for (let k = 1; k < sorted.length; k++) {
    const curr = sorted[k];
    // Overlap at day level (inclusive): curr.from <= last.to
    if (curr.from <= last.to) {
      const lastLabel = `${formatDate(last.from as ISODateString)} – ${formatDate(last.to as ISODateString)}`;

      ctx.addIssue({
        code: 'custom',
        message: lastLabel,
        path: ['pacing_details', curr.idx, 'dates']
      });

      // Keep the one that ends later so we can catch cascaded overlaps
      if (curr.to > last.to) last = curr;
    } else {
      last = curr;
    }
  }

  // Containment check: pacing block dates must fall within the line item date range
  if ('dates' in data && data.dates) {
    const liFrom = data.dates.from;
    const liTo = data.dates.to;

    data.pacing_details.forEach((block, idx) => {
      if (block.dates.from < liFrom || block.dates.to > liTo) {
        ctx.addIssue({
          code: 'custom',
          message: 'Pacing block dates must fall within the line item date range',
          path: ['pacing_details', idx, 'dates']
        });
      }
    });
  }
}

export function refinePacingTotal<T extends PacingTypes>(
  data: T,
  budget: number | undefined,
  ctx: z.RefinementCtx
) {
  if (!data.pacing_details || budget == null) return;
  if (!equalToTwoDecimalPlaces(pacingTotal(data.pacing_details || []), budget)) {
    ctx.addIssue({
      code: 'custom',
      params: { type: 'PACING_MISMATCH' },
      message: 'Pacing does not match the total price',
      path: ['pacing_details']
    });
  }
}

function pacingTotal(pacing: PacingBlockV2[]) {
  return pacing.map(p => p.budget).reduce((a, b) => a + b, 0);
}
